import { useEffect, useState } from "react";

// Debounce utility for resize handling
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export function useMobileOptimized() {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isLowEndDevice: false,
    shouldReduceMotion: false,
    connectionType: "unknown",
    memoryStatus: "unknown",
  });

  const [performanceMetrics, setPerformanceMetrics] = useState({
    jsExecutionTime: 0,
    domContentLoaded: 0,
    firstContentfulPaint: 0,
    largestContentfulPaint: 0,
  });

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent;
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Enhanced device detection
      const isMobile =
        width <= 768 ||
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
      const isTablet = width > 768 && width <= 1024;
      const isDesktop = width > 1024;

      // Enhanced low-end device detection
      const isLowEndDevice =
        navigator.hardwareConcurrency <= 2 ||
        (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
        /Android.*Chrome\/[0-6][0-9]/.test(userAgent) ||
        width <= 480;

      // Enhanced motion preference detection
      const shouldReduceMotion =
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        isLowEndDevice ||
        isMobile;

      // Connection type detection
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      const connectionType = connection ? connection.effectiveType : "unknown";

      // Memory status
      const memoryStatus = navigator.deviceMemory
        ? navigator.deviceMemory >= 8
          ? "high"
          : navigator.deviceMemory >= 4
            ? "medium"
            : "low"
        : "unknown";

      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        isLowEndDevice,
        shouldReduceMotion,
        connectionType,
        memoryStatus,
      });
    };

    // Performance monitoring
    let lcpObserver = null;
    const monitorPerformance = () => {
      if ("performance" in window) {
        const navigation = performance.getEntriesByType("navigation")[0];
        const paint = performance.getEntriesByType("paint");

        const metrics = {
          jsExecutionTime: 0,
          domContentLoaded:
            navigation?.domContentLoadedEventEnd -
              navigation?.domContentLoadedEventStart || 0,
          firstContentfulPaint:
            paint.find((p) => p.name === "first-contentful-paint")?.startTime ||
            0,
          largestContentfulPaint: 0,
        };

        // Monitor LCP
        if ("PerformanceObserver" in window) {
          lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            metrics.largestContentfulPaint = lastEntry.startTime;
            setPerformanceMetrics(metrics);
          });
          lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
        }

        setPerformanceMetrics(metrics);
      }
    };

    checkDevice();
    monitorPerformance();

    // Debounced resize handler - prevents excessive recalculations during resize
    const debouncedCheckDevice = debounce(checkDevice, 200);
    window.addEventListener("resize", debouncedCheckDevice);

    return () => {
      window.removeEventListener("resize", debouncedCheckDevice);
      if (lcpObserver) {
        lcpObserver.disconnect();
      }
    };
  }, []);

  // Performance settings based on device capabilities
  const performanceSettings = {
    // Animation settings
    animationDuration: deviceInfo.shouldReduceMotion ? 0.2 : 0.6,
    animationEasing: deviceInfo.shouldReduceMotion ? "linear" : "ease-out",
    enableParallax: !deviceInfo.isMobile && !deviceInfo.isLowEndDevice,
    enableComplexAnimations:
      deviceInfo.isDesktop && !deviceInfo.shouldReduceMotion,

    // Rendering settings
    enableWebGL: deviceInfo.isDesktop && deviceInfo.memoryStatus !== "low",
    maxParticles: deviceInfo.isLowEndDevice
      ? 50
      : deviceInfo.isMobile
        ? 100
        : 200,
    enableShadows: !deviceInfo.isLowEndDevice,
    enableBlur: !deviceInfo.isLowEndDevice,

    // Loading settings
    imageQuality:
      deviceInfo.connectionType === "slow-2g" ||
      deviceInfo.connectionType === "2g"
        ? "low"
        : "high",
    enableLazyLoading: true,
    preloadDistance: deviceInfo.isMobile ? 100 : 200,

    // Bundle settings
    enableCodeSplitting: true,
    chunkSize: deviceInfo.isLowEndDevice ? 100 : 300,
  };

  // Mobile-optimized animation variants
  const mobileVariants = {
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: {
        duration: performanceSettings.animationDuration,
        ease: performanceSettings.animationEasing,
      },
    },
    slideUp: {
      initial: { opacity: 0, y: deviceInfo.shouldReduceMotion ? 10 : 20 },
      animate: { opacity: 1, y: 0 },
      transition: {
        duration: performanceSettings.animationDuration,
        ease: performanceSettings.animationEasing,
      },
    },
    scale: {
      initial: {
        opacity: 0,
        scale: deviceInfo.shouldReduceMotion ? 0.98 : 0.95,
      },
      animate: { opacity: 1, scale: 1 },
      transition: {
        duration: performanceSettings.animationDuration,
        ease: performanceSettings.animationEasing,
      },
    },
  };

  return {
    ...deviceInfo,
    performanceSettings,
    performanceMetrics,
    mobileVariants,
  };
}
